import { Colors } from '@be-brite/briteui';
import { css } from 'emotion';
import { ArrowUp, Microphone, MicrophoneSlash } from 'phosphor-react';
import { useRef, useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import { Box, Button } from '../shared-components';
import { Text, textTheme } from '../shared-components/text';
import { sendMessage } from './api/chat';
import { useAssistantChat } from './context/AssistantChatProvider';
import {
  formatAssistantResponseWithCitations,
  INPUT_MODE_TEXT,
  INPUT_MODE_VOICE,
  SENDER_ASSISTANT,
  SENDER_USER,
} from './helpers/assistant';
import { useVoiceInput } from './hooks/useVoiceInput';

export const AssistantChatInput = () => {
  // Importing the context to manage chat history and messages.
  const { guide, threadId, isLoading, addMessage } = useAssistantChat();

  // Voice input hook for speech-to-text functionality.
  const {
    isRecording,
    transcript,
    error: voiceError,
    isSupported: isVoiceSupported,
    startRecording,
    stopRecording,
    clearTranscript,
    clearError,
  } = useVoiceInput();

  // Ref to manage the textarea element for dynamic height adjustment.
  const textareaRef = useRef(null);

  // State to manage the message input and form submission status.
  const [message, setMessage] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [inputMode, setInputMode] = useState(INPUT_MODE_TEXT);

  // Update message when transcript changes from voice input.
  useEffect(() => {
    if (!transcript) return;
    setMessage(transcript);
  }, [transcript]);

  // Adjust textarea height immediately after message updates.
  useEffect(() => {
    // Adjusts the height of the textarea to fit its content dynamically.
    const adjustTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // Reset the height to auto to shrink if needed.
      textarea.style.height = 'auto';

      // Get the computed line height, fallback to 20px if not available.
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 20;

      // Calculate the new height based on scrollHeight, ensuring at least one line.
      const newHeight = Math.max(textarea.scrollHeight - lineHeight, lineHeight);

      // Set the textarea height to fit the content.
      textarea.style.height = `${newHeight}px`;
    };

    adjustTextareaHeight();
  }, [message]);

  // Clear voice error after 5 seconds to provide user feedback.
  useEffect(() => {
    if (voiceError) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [voiceError, clearError]);

  // Update input mode based on recording state
  useEffect(() => {
    setInputMode(isRecording ? INPUT_MODE_VOICE : INPUT_MODE_TEXT);
  }, [isRecording]);

  const handleSendMessage = useMutation(() => sendMessage({ guide, threadId, message }), {
    onMutate: () => {
      // Add the user's message to the chat history.
      addMessage({
        sender: SENDER_USER,
        text: message,
      });

      // Reset message state for the next input.
      setMessage('');

      // Disable the textarea to prevent multiple submissions.
      setDisabled(true);
    },
    onSuccess: (message) => {
      // Extract the response and sources from the API response.
      const { ResponseMessage, RelevantSources } = message;

      // Add the assistant's response to the chat history with formatted citations.
      addMessage({
        sender: SENDER_ASSISTANT,
        text: formatAssistantResponseWithCitations(ResponseMessage, RelevantSources),
      });

      // Re-enable the textarea after the message is sent successfully.
      setDisabled(false);
    },
    onError: (error) => {
      console.error('Error sending message:', error);
    },
  });

  // Handler for form submission to send the message.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message) return;
    handleSendMessage.mutate();
  };

  // Handler for textarea keydown to support Enter key submission.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  // Handler for voice recording toggle to start or stop recording.
  const handleVoiceToggle = () => {
    // If not recording, start recording and return early.
    if (!isRecording) {
      startRecording();
      return;
    }

    // Stop recording if currently recording.
    stopRecording();

    // If transcript has content, send the message.
    const trimmedTranscript = transcript?.trim();
    if (trimmedTranscript) {
      handleSendMessage.mutate();
    }

    // Clear transcript regardless of whether it was sent.
    clearTranscript();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Display "Thinking..." indicator when message is being processed. */}
      {disabled && (
        <Box
          css={`
            padding-bottom: 12px;
          `}
        >
          <Text helper color={Colors.Gray400}>
            Thinking...
          </Text>
        </Box>
      )}
      {/* Display voice input error messages. */}
      {voiceError && (
        <Box
          css={`
            padding-bottom: 12px;
          `}
        >
          <Text helper color={Colors.Red100}>
            {voiceError}
          </Text>
        </Box>
      )}
      {/* Display recording indicator with live transcript preview. */}
      {isRecording && (
        <Box
          css={`
            padding-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <Box
            css={`
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background-color: ${Colors.Red100};
              animation: pulse 1.5s ease-in-out infinite;
            `}
          />
          <Text helper color={Colors.Gray600}>
            Listening...
          </Text>
        </Box>
      )}
      {/* Main input container with textarea and action buttons. */}
      <Box
        css={`
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 12px;
          background-color: ${disabled ? Colors.Gray100 : Colors.White};
          border: 1px solid ${inputMode === INPUT_MODE_VOICE ? Colors.Red100 : Colors.Gray300};
          border-radius: 8px;
          transition: border-color 0.2s ease;
          ${inputMode === INPUT_MODE_VOICE
            ? `
            box-shadow: 0 0 0 1px ${Colors.Red100};
          `
            : ''}
        `}
      >
        <textarea
          ref={textareaRef}
          disabled={isLoading || disabled}
          autoFocus
          rows={1}
          className={css`
            background-color: transparent;
            padding: 12px;
            max-height: 168px;
            width: 100%;
            border: none;
            outline: none;
            resize: none;
            overflow: hidden;
            ${textTheme?.label}

            ::placeholder {
              color: ${Colors.Gray400};
            }
          `}
          placeholder="Ask a question..."
          onInput={(e) => setMessage(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          value={message}
        />
        {/* Action buttons container for voice and send functionality. */}
        <Box
          css={`
            display: flex;
            justify-content: flex-end;
            padding: 12px;
            width: 100%;
            gap: 4px;
          `}
        >
          {/* Voice recording button - only shown if voice input is supported. */}
          {isVoiceSupported && (
            <Button
              styles="icon"
              css={`
                width: 32px;
                height: 32px;
                padding: 4px;
                background: ${isRecording ? Colors.Red100 : Colors.White};
                transition: all 0.2s ease;
                position: relative;

                &:hover {
                  background: ${isRecording ? Colors.Red200 : Colors.Gray300};
                  border-color: ${isRecording ? Colors.Red200 : Colors.Gray300};
                }

                ${isRecording
                  ? `
                  box-shadow: 0 0 8px rgba(239, 68, 68, 0.3);
                `
                  : ''}
              `}
              disabled={disabled}
              type="button"
              onClick={handleVoiceToggle}
              aria-label={isRecording ? 'Stop voice recording' : 'Start voice recording'}
            >
              {isRecording ? (
                <MicrophoneSlash size={16} weight="bold" color="white" />
              ) : (
                <Microphone size={16} weight="bold" />
              )}
            </Button>
          )}
          {/* Send message button - disabled when no message is entered. */}
          <Button
            styles="icon"
            css={`
              width: 32px;
              height: 32px;
              padding: 4px;
              background: ${Colors.Gray200};
              border: 0.5px solid ${Colors.Gray200};
              border-radius: 5px;
            `}
            disabled={!message || isRecording}
            type="submit"
            aria-label="Send message"
          >
            <ArrowUp size={16} weight="bold" />
          </Button>
        </Box>
      </Box>
      {/* CSS animation for the recording indicator pulse effect. */}
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
            100% {
              opacity: 1;
            }
          }
        `}
      </style>
    </form>
  );
};
